import { LongGameError, } from '@long-game/common';
import { runInAction } from 'mobx';
import { AbstractGameSuite } from './AbstractGameSuite.js';
import { getDevModeTurns, getPlayers, getPostgame, getPublicRound, getSummary, startGame, } from './api.js';
import { connectToSocket } from './socket.js';
export const ROOT_CHAT_SCENE_ID = 'null';
/**
 * Implements AbstractGameSuite against a backend game session.
 */
export class GameSessionSuite extends AbstractGameSuite {
    ctx;
    static load = async (gameSessionId, gameModules, publicSdk) => {
        const details = await publicSdk.getGameSessionDetails.run({
            id: gameSessionId,
        });
        return new GameSessionSuite(details, {
            socket: connectToSocket(details.id),
            gameModules,
        });
    };
    isHotseat = false;
    constructor(init, ctx) {
        super(init, { gameModules: ctx.gameModules });
        this.ctx = ctx;
        runInAction(() => (this.playerId = init.playerId));
        this.subscribeSocket();
        this.#subscribeWindowEvents();
    }
    connect = () => {
        console.log('connecting...', this.instanceId, 'socket', this.ctx.socket.id);
        return this.ctx.socket.reconnect();
    };
    disconnect = async () => {
        console.log('disconnecting...', this.instanceId, 'socket', this.ctx.socket.id);
        // submit turn immediately if delayed
        if (this.isTurnSubmitDelayed) {
            await this.submitTurn().catch(() => { });
        }
        this.ctx.socket.disconnect();
    };
    actuallySubmitTurn = async (data) => {
        const response = await this.ctx.socket.request({
            type: 'submitTurn',
            turnData: data,
        });
        if (response.type === 'error') {
            throw new LongGameError(LongGameError.Code.Unknown, response.message);
        }
    };
    actuallySendChat = async (message) => {
        await this.ctx.socket.request({
            type: 'sendChat',
            message: message,
        });
    };
    actuallyLoadMoreChat = async (nextToken, sceneId = null) => {
        this.ctx.socket.send({
            type: 'requestChat',
            nextToken,
            sceneId,
        });
    };
    toggleChatReaction = async (chatMessage, reaction) => {
        const isOn = chatMessage.reactions[reaction]?.includes(this.playerId);
        await this.ctx.socket.request({
            type: 'toggleChatReaction',
            chatMessageId: chatMessage.id,
            isOn: !isOn,
            reaction,
        });
    };
    loadRound = (roundIndex) => {
        return getPublicRound(this.gameSessionId, roundIndex);
    };
    startGame = async () => {
        await startGame(this.gameSessionId);
    };
    voteForGame = (gameId) => {
        this.ctx.socket.send({
            type: 'voteForGame',
            gameId,
            remove: false,
        });
    };
    removeVoteForGame = (gameId) => {
        this.ctx.socket.send({
            type: 'voteForGame',
            gameId,
            remove: true,
        });
    };
    subscribeSocket = () => {
        this.ctx.socket.subscribe('chat', this.onChat);
        this.ctx.socket.subscribe('playerStatusChange', this.onPlayerStatusChange);
        this.ctx.socket.subscribe('roundChange', this.onRoundChange);
        this.ctx.socket.subscribe('statusChange', this.onStatusChange);
        this.ctx.socket.subscribe('gameChange', this.onGameChange);
        this.ctx.socket.subscribe('membersChange', this.onMembersChange);
        this.ctx.socket.subscribe('turnPlayed', this.onTurnPlayed);
        this.ctx.socket.subscribe('nextRoundScheduled', this.onNextRoundScheduled);
        this.ctx.socket.subscribe('playerVoteForGame', this.onPlayerVoteForGame);
        this.ctx.socket.subscribe('gameStarting', this.onGameStarting);
    };
    getPostgame = async () => {
        const res = await getPostgame(this.gameSessionId);
        return res.globalState;
    };
    getMembers() {
        return getPlayers(this.gameSessionId);
    }
    getGameData = async () => {
        const summary = await getSummary(this.gameSessionId);
        const gameDefinition = await this.ctx.gameModules.getGameDefinition(summary.gameId || 'empty', summary.gameVersion || 'v1');
        return {
            ...summary,
            gameDefinition,
        };
    };
    /**
     * Only works in dev mode
     */
    resetGame = async () => {
        this.ctx.socket.send({
            type: 'resetGame',
        });
    };
    switchPlayer() {
        throw new LongGameError(LongGameError.Code.BadRequest, 'Switching players is not allowed as this is not a hotseat game. This is a bug!');
    }
    getDevModeTurns = async () => {
        if (!this.gameSessionId) {
            throw new LongGameError(LongGameError.Code.BadRequest, 'Cannot get dev mode turns without a game session ID');
        }
        return getDevModeTurns(this.gameSessionId);
    };
    #subscribeWindowEvents = () => {
        // FIXME: causing infinite suspense???
        // const onVisibilityChange = () => {
        //   if (document.visibilityState === 'visible') {
        //     this.onGameChange();
        //   }
        // };
        // document.addEventListener('visibilitychange', onVisibilityChange);
        // this.#disposes.push(() => {
        //   document.removeEventListener('visibilitychange', onVisibilityChange);
        // });
    };
}
//# sourceMappingURL=GameSessionSuite.js.map